import {
  ChangeEvent,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { TextField, useThemeProps } from '@mui/material';
import { Search } from '@mui/icons-material';

const SearchField = forwardRef<any, any>((inProps, ref) => {
  const { value, onChange, delay, ...otherProps } = useThemeProps({
    props: inProps,
    name: 'MuiTextField',
  });

  const useDebouncedCallback = (
    func: (...args: any[]) => unknown,
    wait: number
  ) => {
    // Use a ref to store the timeout between renders
    // and prevent changes to it from causing re-renders
    const timeout = useRef<NodeJS.Timeout>();

    return useCallback(
      (...args: unknown[]) => {
        const later = () => {
          if (timeout.current) clearTimeout(timeout.current);
          func(...args);
        };

        if (timeout.current) clearTimeout(timeout.current);
        timeout.current = setTimeout(later, wait);
      },
      [func, wait]
    );
  };

  const [inputValue, setInputValue] = useState(value);

  const callback = (e: ChangeEvent<HTMLInputElement>) => {
    if (onChange) onChange(e);
  };

  // Update internal state when props change
  useEffect(() => setInputValue(value), [value]);

  const debounceCallback = useDebouncedCallback(callback, delay);

  return (
    <TextField
      ref={ref}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
        debounceCallback(e);
      }}
      value={inputValue}
      variant="filled"
      InputProps={{
        startAdornment: <Search /> || otherProps.startAdornment,
        endAdornment: otherProps.endAdornment,
        sx: {
          backgroundColor: 'sky.main',
          paddingLeft: 2.25,
          '& > input': { paddingLeft: 1.5 },
          width: 500,
          mb: 2,
        },
      }}
      placeholder="Search"
      {...otherProps}
    />
  );
});

export default SearchField;
